import type { Dispatch, SetStateAction } from "react";
import type { FillMark } from "../extractFill";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import type { FormState, RouteDirection, TimeMode } from "../types";
import type { Errors } from "../validation";
import { FillBadge, SelectInput } from "./fields";
import { TimeInput } from "./TimeInput";

const KEYS = {
  load: { type: "loadType", from: "loadFrom", until: "loadUntil", fixed: "loadFixed" },
  unload: { type: "unloadType", from: "unloadFrom", until: "unloadUntil", fixed: "unloadFixed" },
} as const;

function isTimeMode(value: string): value is TimeMode {
  return value === "window" || value === "fixed";
}

interface Props {
  phase: RouteDirection;
  time: FormState["time"];
  setForm: Dispatch<SetStateAction<FormState>>;
  errors: Errors;
  required: boolean;
  fillStatus?: FillMark;
}

export function TimeWindowFields({
  phase,
  time,
  setForm,
  errors,
  required,
  fillStatus,
}: Props) {
  const copy = useQuoteCopy();
  const keys = KEYS[phase];
  const isFixed = time[keys.type] === "fixed";
  const title = phase === "load" ? copy.t("services.loadingTime") : copy.t("services.unloadingTime");

  const patch = (key: keyof FormState["time"], value: string) => {
    setForm((f) => ({ ...f, time: { ...f.time, [key]: value } }));
  };

  return (
    <div className="nv-time-block">
      <div className="nv-label-row">
        <span className="nv-label">
          {title}
          {required && (
            <span className="nv-req" aria-hidden="true">
              {"\u00A0*"}
            </span>
          )}
        </span>
        {fillStatus && <FillBadge mark={fillStatus} />}
      </div>
      <SelectInput
        id={`nv-time-${phase}-type`}
        className="nv-time-mode"
        aria-label={copy.t("services.timeTypeAria", { title })}
        value={time[keys.type]}
        onChange={(v) => {
          if (!isTimeMode(v)) return;
          setForm((f) => ({
            ...f,
            time: {
              ...f.time,
              [keys.type]: v,
              ...(v === "fixed"
                ? { [keys.from]: "", [keys.until]: "" }
                : { [keys.fixed]: "" }),
            },
          }));
        }}
      >
        <option value="window">{copy.t("services.window")}</option>
        <option value="fixed">{copy.t("services.fixed")}</option>
      </SelectInput>
      <div className="nv-row nv-time-row">
        {isFixed ? (
          <TimeInput
            id={`nv-time-${phase}-fixed`}
            label={copy.t("services.at")}
            required={required}
            error={errors[keys.fixed]}
            value={time[keys.fixed]}
            onChange={(v) => patch(keys.fixed, v)}
          />
        ) : (
          <>
            <TimeInput
              id={`nv-time-${phase}-from`}
              label={copy.t("services.from")}
              required={required}
              error={errors[keys.from]}
              value={time[keys.from]}
              onChange={(v) => patch(keys.from, v)}
            />
            <TimeInput
              id={`nv-time-${phase}-until`}
              label={copy.t("services.to")}
              required={required}
              error={errors[keys.until]}
              value={time[keys.until]}
              onChange={(v) => patch(keys.until, v)}
            />
          </>
        )}
      </div>
    </div>
  );
}
